// SPDX-License-Identifier: MIT
// Desktop "Connecter mon Claude" — the 0-friction browser-redirect login.
//
// The desktop app runs inference on the user's OWN Claude subscription via the
// local `claude` CLI (claude_prompt). Before that can work the user must link
// their Claude account once. Instead of telling them to open a terminal, this
// overlay does it from the app: one button → the Rust `claude_login` command runs
// `claude auth login` → the system browser opens the Claude OAuth flow → the user
// accepts → the token lands in ~/.claude/.credentials.json. Claude Code owns the
// token; Genesis never sees it (BYOAI-absolute). Same process as in a terminal,
// zero friction.
//
// Self-contained + desktop-only: it talks to Tauri via the global invoke and is
// mounted from main.ts only when detectPlatform() === 'desktop'. The web build
// never calls it.
import { tauriInvoke } from '../platform/tauri';
import { detectLocale } from '../i18n/detect';

// `email` is the Claude account the local CLI is signed into (from
// `claude auth status --json`). Surfaced so the app can later compare it against
// the signed-in Genesis web account and, if they differ, show a NON-blocking
// "different account" notice (never a hard gate). null when logged out / on macOS
// Keychain creds where the file probe can't read it.
export interface ClaudeAuth { installed: boolean; linked: boolean; email?: string | null; detail?: string; }

export async function claudeAuthStatus(): Promise<ClaudeAuth> {
  try {
    const v = JSON.parse((await tauriInvoke()('claude_auth_status')) as string) as ClaudeAuth;
    return { installed: !!v.installed, linked: !!v.linked, email: v.email ?? null, detail: v.detail };
  } catch (e) {
    return { installed: false, linked: false, email: null, detail: `invoke failed: ${String(e)}` };
  }
}

// Returns the Claude OAuth authorization URL. The Rust side already opened it in the
// browser; we surface it so the app can show a copyable link (browser-open fallback).
export async function claudeLogin(): Promise<string> {
  return (await tauriInvoke()('claude_login')) as string;
}

const STR = {
  fr: {
    checking: 'Vérification de ton Claude…',
    linkTitle: 'Connecte ton Claude',
    linkBody: 'Un clic, le navigateur s’ouvre, tu acceptes — et ton abonnement Claude alimente tes idées. Genesis ne voit jamais ton compte.',
    linkBtn: 'Connecter mon Claude',
    opening: 'Navigateur ouvert — termine la connexion, puis reviens ici.',
    awaitTitle: 'Autorise ton abonnement Claude',
    awaitBody: 'Ton navigateur vient de s’ouvrir sur la page de connexion Claude. Connecte-toi à ton compte (Pro / Max), accepte, puis reviens ici et clique « J’ai terminé ».',
    linkIntro: 'Le navigateur ne s’est pas ouvert ? Copie ce lien et colle-le dans ton navigateur :',
    copyLink: 'Copier',
    copied: 'Copié ✓',
    awaitBtn: 'J’ai terminé — vérifier',
    installTitle: 'Installe Claude Code',
    installBody: 'Le desktop utilise ton Claude local. Installe-le une fois, puis reviens :',
    installStep1: 'Ouvre un terminal (PowerShell / Terminal)',
    installStep2: 'npm install -g @anthropic-ai/claude-code',
    recheck: 'J’ai installé — re-vérifier',
    retry: 'Réessayer',
    failed: 'La connexion n’a pas abouti. Réessaie.',
    skip: 'Plus tard',
    sovereignNote: 'Ton IA, ton abonnement — illimité, en local.',
  },
  en: {
    checking: 'Checking your Claude…',
    linkTitle: 'Connect your Claude',
    linkBody: 'One click, the browser opens, you accept — and your Claude subscription powers your ideas. Genesis never sees your account.',
    linkBtn: 'Connect my Claude',
    opening: 'Browser opened — finish signing in, then come back here.',
    awaitTitle: 'Authorize your Claude subscription',
    awaitBody: 'Your browser just opened the Claude sign-in page. Sign in to your account (Pro / Max), accept, then come back here and click “I’m done”.',
    linkIntro: 'Browser didn’t open? Copy this link and paste it into your browser:',
    copyLink: 'Copy',
    copied: 'Copied ✓',
    awaitBtn: 'I’m done — check',
    installTitle: 'Install Claude Code',
    installBody: 'The desktop app uses your local Claude. Install it once, then come back:',
    installStep1: 'Open a terminal (PowerShell / Terminal)',
    installStep2: 'npm install -g @anthropic-ai/claude-code',
    recheck: 'I installed it — re-check',
    retry: 'Try again',
    failed: 'Sign-in didn’t complete. Try again.',
    skip: 'Later',
    sovereignNote: 'Your AI, your subscription — unlimited, local.',
  },
} as const;

// Persisted "Plus tard" choice. Once the user dismisses the startup overlay we
// must NEVER hard-gate the app on Claude again — they connect from /connecter at
// will. This is what stops the overlay "qui ne disparaît pas" from re-appearing on
// every launch (P2). Cleared automatically the moment Claude is detected as linked.
const CLAUDE_CONNECT_DISMISS_KEY = 'genesis.claudeConnect.dismissedAt';
function claudeConnectDismissed(): boolean {
  try { return !!localStorage.getItem(CLAUDE_CONNECT_DISMISS_KEY); } catch { return false; }
}
function setClaudeConnectDismissed(on: boolean): void {
  try {
    if (on) localStorage.setItem(CLAUDE_CONNECT_DISMISS_KEY, String(Date.now()));
    else localStorage.removeItem(CLAUDE_CONNECT_DISMISS_KEY);
  } catch { /* private mode / no storage — fail open (overlay still skippable) */ }
}

/**
 * Desktop-only startup gate. Resolves immediately — NO overlay — when Claude is
 * already linked, OR when the user previously clicked "Plus tard" (the app stays
 * fully usable; Claude can be connected later from /connecter). Otherwise mounts a
 * dismissible overlay (button / Escape / backdrop click all close it) and resolves
 * once the user links or dismisses. Safe no-op shape on the web — callers must
 * guard with detectPlatform() === 'desktop' so the web build never invokes Tauri.
 */
export async function ensureClaudeLinked(): Promise<void> {
  const S = STR[detectLocale() === 'en' ? 'en' : 'fr'];
  const first = await claudeAuthStatus();
  if (first.linked) { setClaudeConnectDismissed(false); return; }
  if (claudeConnectDismissed()) return; // user already said "Plus tard" — don't nag
  let lastDetail = first.detail ?? '';

  return new Promise<void>((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'desktop-connect';
    overlay.setAttribute('data-component', 'desktop-connect');
    overlay.innerHTML = `<div class="desktop-connect__card" role="dialog" aria-modal="true"></div>`;
    const card = overlay.querySelector<HTMLElement>('.desktop-connect__card')!;
    document.body.appendChild(overlay);

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    // close() = leave the overlay (used on successful link); dismiss() also remembers
    // the "Plus tard" so we never auto-show it again. Both unhook the key listener.
    const close = () => { document.removeEventListener('keydown', onKey); overlay.remove(); resolve(); };
    const dismiss = () => { setClaudeConnectDismissed(true); close(); };
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) dismiss(); }); // backdrop click
    const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

    const renderLink = () => {
      card.innerHTML = `
        <span class="desktop-connect__spark" aria-hidden="true">✦</span>
        <h2>${esc(S.linkTitle)}</h2>
        <p>${esc(S.linkBody)}</p>
        <button class="desktop-connect__btn" data-component="dc-login">${esc(S.linkBtn)}</button>
        <p class="desktop-connect__note">${esc(S.sovereignNote)}</p>
        <button class="desktop-connect__skip" data-component="dc-skip">${esc(S.skip)}</button>
        ${lastDetail ? `<p class="desktop-connect__debug" data-component="dc-debug">${esc(lastDetail)}</p>` : ''}`;
      card.querySelector<HTMLButtonElement>('[data-component="dc-login"]')!.addEventListener('click', onLogin);
      card.querySelector<HTMLButtonElement>('[data-component="dc-skip"]')!.addEventListener('click', dismiss);
    };

    const renderInstall = () => {
      card.innerHTML = `
        <h2>${esc(S.installTitle)}</h2>
        <p>${esc(S.installBody)}</p>
        <ol class="desktop-connect__steps">
          <li>${esc(S.installStep1)}</li>
          <li><code>${esc(S.installStep2)}</code></li>
        </ol>
        <button class="desktop-connect__btn" data-component="dc-recheck">${esc(S.recheck)}</button>
        <button class="desktop-connect__skip" data-component="dc-skip">${esc(S.skip)}</button>
        ${lastDetail ? `<p class="desktop-connect__debug" data-component="dc-debug">${esc(lastDetail)}</p>` : ''}`;
      card.querySelector<HTMLButtonElement>('[data-component="dc-recheck"]')!.addEventListener('click', recheck);
      card.querySelector<HTMLButtonElement>('[data-component="dc-skip"]')!.addEventListener('click', dismiss);
    };

    const renderBusy = (msg: string) => {
      card.innerHTML = `<div class="desktop-connect__spinner" aria-hidden="true"></div><p>${esc(msg)}</p>`;
    };

    const onLogin = async () => {
      // Rust runs `claude auth login`, captures the OAuth URL, opens the browser, and
      // returns the URL. We show it as a copyable link (browser-open fallback) and the
      // user finishes in the browser, then re-checks. claude owns the token (BYOAI).
      renderBusy(S.opening);
      try {
        const url = await claudeLogin();
        renderAwaitLogin(url);
      } catch {
        renderFailed();
      }
    };

    const renderAwaitLogin = (url: string) => {
      card.innerHTML = `
        <span class="desktop-connect__spark" aria-hidden="true">✦</span>
        <h2>${esc(S.awaitTitle)}</h2>
        <p>${esc(S.awaitBody)}</p>
        ${url ? `
        <p style="margin:.7rem 0 .3rem;font-size:.82rem;opacity:.85">${esc(S.linkIntro)}</p>
        <div style="display:flex;gap:6px;align-items:center">
          <input data-component="dc-url" readonly value="${esc(url)}" style="flex:1;min-width:0;font:12px/1.4 ui-monospace,monospace;padding:7px 9px;border:1px solid #d9cfc0;border-radius:7px;background:#fff;color:#3a3a3a">
          <button data-component="dc-copy" style="white-space:nowrap;padding:7px 11px;border-radius:7px;border:1px solid #e8521f;background:#fff;color:#e8521f;font-weight:700;cursor:pointer">${esc(S.copyLink)}</button>
        </div>` : ''}
        <button class="desktop-connect__btn" data-component="dc-done">${esc(S.awaitBtn)}</button>
        <button class="desktop-connect__skip" data-component="dc-skip">${esc(S.skip)}</button>`;
      const copyBtn = card.querySelector<HTMLButtonElement>('[data-component="dc-copy"]');
      if (url && copyBtn) {
        copyBtn.addEventListener('click', () => {
          const inp = card.querySelector<HTMLInputElement>('[data-component="dc-url"]')!;
          inp.focus();
          inp.select();
          const done = () => { copyBtn.textContent = S.copied; };
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url).then(done).catch(() => { try { document.execCommand('copy'); done(); } catch { /* noop */ } });
          } else {
            try { document.execCommand('copy'); done(); } catch { /* noop */ }
          }
        });
      }
      card.querySelector<HTMLButtonElement>('[data-component="dc-done"]')!.addEventListener('click', recheck);
      card.querySelector<HTMLButtonElement>('[data-component="dc-skip"]')!.addEventListener('click', dismiss);
    };

    const renderFailed = () => {
      card.innerHTML = `
        <h2>${esc(S.linkTitle)}</h2>
        <p class="desktop-connect__err">${esc(S.failed)}</p>
        <button class="desktop-connect__btn" data-component="dc-login">${esc(S.retry)}</button>
        <button class="desktop-connect__skip" data-component="dc-skip">${esc(S.skip)}</button>`;
      card.querySelector<HTMLButtonElement>('[data-component="dc-login"]')!.addEventListener('click', onLogin);
      card.querySelector<HTMLButtonElement>('[data-component="dc-skip"]')!.addEventListener('click', dismiss);
    };

    const recheck = async () => {
      renderBusy(S.checking);
      const st = await claudeAuthStatus();
      lastDetail = st.detail ?? lastDetail;
      if (st.linked) { setClaudeConnectDismissed(false); close(); return; }
      st.installed ? renderLink() : renderInstall();
    };

    first.installed ? renderLink() : renderInstall();
  });
}
